import React, { useState, useEffect } from 'react';
import { parseTimeToSeconds, formatSecondsToMMSS } from '../../utils/videoTimingUtils';
import { VideoPlayerMethods } from '../video/types';

interface BreakEventTimingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (timingData: {
    videoTimeSeconds: number | null;
    matchMinute: number;
    videoId?: string | null;
  }) => void;
  eventType: 'start_2nd_half' | 'start_extra_time_1st_half' | 'start_extra_time_2nd_half';
  eventLabel: string;
  videoPlayerRef?: React.RefObject<VideoPlayerMethods | null>;
  currentVideoId?: string | null;
  defaultMatchMinute?: number; // Default match minute for this event (e.g., 45 for 2nd half)
}

const BreakEventTimingModal: React.FC<BreakEventTimingModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  eventType,
  eventLabel,
  videoPlayerRef,
  currentVideoId,
  defaultMatchMinute = 45
}) => {
  const [matchStartVideoTimeSeconds, setMatchStartVideoTimeSeconds] = useState<number | null>(null);
  const [matchStartMatchMinute, setMatchStartMatchMinute] = useState<number>(defaultMatchMinute);
  const [videoTimeInput, setVideoTimeInput] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [expandedTiming, setExpandedTiming] = useState(false);

  // Auto-capture video time when modal opens (if video player available)
  useEffect(() => {
    if (isOpen && videoPlayerRef?.current && currentVideoId) {
      try {
        const currentTime = videoPlayerRef.current.getCurrentTime();
        if (currentTime > 0) {
          setMatchStartVideoTimeSeconds(currentTime);
          setVideoTimeInput(formatSecondsToMMSS(currentTime));
        }
      } catch (error) {
        console.warn('Could not capture video time:', error);
      }
    }
  }, [isOpen, videoPlayerRef, currentVideoId]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setMatchStartVideoTimeSeconds(null);
      setMatchStartMatchMinute(defaultMatchMinute);
      setVideoTimeInput('');
      setError(null);
      setExpandedTiming(false);
    }
  }, [isOpen, defaultMatchMinute]);

  const handleVideoTimeChange = (value: string) => {
    setVideoTimeInput(value);
    const seconds = parseTimeToSeconds(value);
    setMatchStartVideoTimeSeconds(seconds);
    setError(null);
  };

  const handleMatchMinuteChange = (value: string) => {
    const minute = value === '' ? defaultMatchMinute : parseInt(value, 10);
    if (!isNaN(minute) && minute >= 0) {
      setMatchStartMatchMinute(minute);
    }
  };

  const handleConfirm = () => {
    // Video timing is optional - allow confirmation even without video time
    // Only validate if user has entered something (expanded timing section was used)
    if (expandedTiming && videoTimeInput.trim() !== '') {
      const parsed = parseTimeToSeconds(videoTimeInput);
      if (parsed === null || parsed < 0) {
        setError('Please enter a valid video time format (MM:SS or seconds)');
        return;
      }
    }

    onConfirm({
      videoTimeSeconds: expandedTiming && matchStartVideoTimeSeconds !== null ? matchStartVideoTimeSeconds : null,
      matchMinute: matchStartMatchMinute,
      videoId: expandedTiming && matchStartVideoTimeSeconds !== null ? currentVideoId || null : null
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirm {eventLabel}</h3>
        <p className="text-gray-600 mb-6">
          Are you sure you want to {eventLabel.toLowerCase()}? This will update the match timeline.
        </p>

        {/* Advanced Timing Section - Collapsible */}
        <div className="mb-6 border border-gray-200 rounded-md">
          <button
            type="button"
            onClick={() => setExpandedTiming(!expandedTiming)}
            className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <span>Video Timing (Optional)</span>
            <svg
              className={`w-4 h-4 text-gray-500 transition-transform ${expandedTiming ? 'transform rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {expandedTiming && (
            <div className="px-3 pb-3 space-y-3 border-t border-gray-200 pt-3">
              {/* Match Start Time in Video */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Video Time When {eventLabel} Starts
                </label>
                <input
                  type="text"
                  value={videoTimeInput}
                  onChange={(e) => handleVideoTimeChange(e.target.value)}
                  placeholder="e.g., 2730 or 45:30"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Current video time when this event occurs. Format: MM:SS or seconds (e.g., "2730" or "45:30")
                </p>
              </div>
              
              {/* Match Minute When Event Starts */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Match Minute When {eventLabel} Starts
                </label>
                <input
                  type="number"
                  min="0"
                  value={matchStartMatchMinute}
                  onChange={(e) => handleMatchMinuteChange(e.target.value)}
                  placeholder={`${defaultMatchMinute} (default)`}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Match minute when this event occurs (e.g., 45 for 2nd half, 90 for extra time)
                </p>
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="flex space-x-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export default BreakEventTimingModal;

